import styles from './edit-product-dialog.module.scss';
import { useContext, useState } from 'react';

import { useGetRequestNewHook, useMakeRequestHook } from '../../requestUtils';
import { CheckboxLabel, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, MenuItem, MultiLineTextField, MultiSelect, PrimaryButton, Select, SnackBarContext, TextField } from '@frontend-workspace/react-shared-lib';
import { IRelationProduct } from '../../_models/relation-product';
import { IContractItemInfos, ISupportCategory } from '../../_models/contract-item-infos';
import { useTranslation } from 'react-i18next';
import { use } from 'i18next';

/* eslint-disable-next-line */
export interface EditProductDialogProps {
  open: boolean,
  onClose: () => void,
  allProducts: IRelationProduct[],
  productName: string
  editProduct?: IRelationProduct,
}

export function EditProductDialog(props: EditProductDialogProps) {
  const makeRequest = useMakeRequestHook();
  const [relatedProductIds, setRelatedProductIds] = useState<string[]>(props.editProduct ? props.editProduct.relatedProductIds : []);
  const [moduleIds, setModuleIds] = useState<string[]>(props.editProduct ? props.editProduct.moduleIds : []);
  const [vismaIds, setVismaIds] = useState<string | string[]>(props.editProduct ? props.editProduct.vismaItemIds : "");
  const [mainProductId, setMainProductId] = useState<string>(props.editProduct ? props.editProduct.mainProductId : "");
  const [productName, setProductName] = useState<string>(props.editProduct ? props.editProduct.productName : "");
  const [productCode, setProductCode] = useState<string>(props.editProduct ? props.editProduct.productCode : "");
  const [doesNotHaveNimbusLicensing, setDoesNotHaveNimbusLicensing] = useState<boolean>(props.editProduct?.doesNotHaveNimbusLicensing || false);
  const [webAppUrl, setWebAppUrl] = useState<string>(props.editProduct ? props.editProduct.webAppUrl : "");
  const [iconUrl, setIconUrl] = useState<string>(props.editProduct ? props.editProduct.iconUrl : "");
  const [ticketCategoryId, setTicketCategoryId] = useState<number | undefined>(props.editProduct ? props.editProduct.ticketCategoryId : undefined);
  const [description, setDescription] = useState<string>(props.editProduct?.description || "");
  const [requirements, setRequirements] = useState<string>(props.editProduct?.requirements || "");
  const [productPageUrl, setProductPageUrl] = useState<string>(props.editProduct?.productPageUrl || "");

  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const { data: vismaItems }: { data: IContractItemInfos[] } = useGetRequestNewHook(`contractItemInfos`, []);
  const vismaIdsInUse = props.allProducts.reduce((vismaIds: string[], p) => vismaIds.concat(p.vismaItemIds), []);
  const freeVismaItems = vismaItems.filter(i => !vismaIdsInUse.includes(i.id) || props.editProduct?.vismaItemIds.includes(i.id));
  const { data: categories }: { data: ISupportCategory[] } = useGetRequestNewHook(`supportCategories`, []);
  const selectedVismaItems = vismaItems.filter(item => vismaIds.includes(item.id));
  const selectedModules = props.allProducts.filter(item => moduleIds.includes(item.id));
  const selectedRelatedProducts = props.allProducts.filter(item => relatedProductIds.includes(item.id));

  const handleVismaIdChange = (event: any) => {
    const vismaIds = event.target.value.map((t: IContractItemInfos) => t.id);
    setVismaIds(vismaIds);
  };

  const handleModuleChange = (event: any) => {
    const moduleIds = event.target.value.map((t: IRelationProduct) => t.id);
    setModuleIds(moduleIds);
  };

  const handleRelatedProductChange = (event: any) => {
    const relatedIds = event.target.value.map((t: IRelationProduct) => t.id);
    setRelatedProductIds(relatedIds);
  };

  const handleLicensingChange = (event: any) => setDoesNotHaveNimbusLicensing(event.currentTarget.checked);
  const handleParentChange = (event: any) => setMainProductId(event.target.value);
  const handleSupportTicketChange = (event: any) => setTicketCategoryId(event.target.value);
  const handleProductNameChange = (event: any) => setProductName(event.target.value);
  const handleWebUrlChange = (event: any) => setWebAppUrl(event.target.value);
  const handleIconUrlChange = (event: any) => setIconUrl(event.target.value);
  const handleDescriptionChange = (event: any) => setDescription(event.target.value);
  const handleRequirementsChange = (event: any) => setRequirements(event.target.value);
  const handleProductPageUrlChange = (event: any) => setProductPageUrl(event.target.value);
  const handleProductCodeChange = (event: any) => setProductCode(event.target.value);

  const handleProductUpdate = () => {
    const body = {
      productName: productName,
      vismaIds: vismaIds,
      moduleIds: moduleIds,
      relatedProductIds: relatedProductIds,
      iconUrl: iconUrl,
      mainProductId: mainProductId,
      webAppUrl: webAppUrl,
      ticketCategoryId,
      doesNotHaveNimbusLicensing,
      description,
      productCode,
      requirements,
      productPageUrl
    };
    if (props.editProduct) {
      makeRequest(`relationProducts/${props.editProduct.id}`, `PUT`, body).then(result => {
        setSnackbarMessage({ content: t("editProduct.changeDone"), type: "success" });
        props.onClose();
      }).catch(() => setSnackbarMessage({ content: t("editProduct.failedToExecuteChange"), type: "error" }));
    } else {
      makeRequest(`relationProducts/`, 'POST', body).then(result => {
        setSnackbarMessage({ content: t("editProduct.newProductCreated"), type: "success" });
        props.onClose();
      }).catch(() => setSnackbarMessage({ content: t("editProduct.couldNotCreateNewProduct"), type: "error" }));
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle className='mb-4'>
        {!props.editProduct ? t("standard.addProduct") : (<span>{t("standard.changeProduct")}: <span className='MuiTypography-body3'>{props.productName}</span></span>)}
      </DialogTitle>
      <DialogContent>
        <div className='mb-3'>
          <TextField
            placeholder={`${t("editProduct.example")}: Focus Kontruksjon 3D`}
            value={productName || ""}
            onChange={handleProductNameChange}
            fullWidth
            label={t("standard.productName")}
            name='productName'
          />
        </div>
        <div className='mb-3'>
          <TextField
            value={productCode || ""}
            placeholder={`${t("editProduct.example")}: FKON`}
            onChange={handleProductCodeChange}
            fullWidth
            label={t("standard.productCode")}
            name='productCode'
          />
        </div>
        <div className='mb-3'>
          <Select
            label={t("editProduct.mainProduct")}
            name='mainProduct'
            fullWidth
            value={mainProductId || ""}
            onChange={handleParentChange}>
            {props.allProducts.map(d => <MenuItem value={d.id} key={d.id}>{d.productName}</MenuItem>)}
          </Select>
        </div>
        <div className='mb-3'>
          <TextField
            value={webAppUrl || ""}
            onChange={handleWebUrlChange}
            placeholder={`${t("editProduct.example")}: https://gml.focus.no`}
            fullWidth
            label={t("editProduct.urlToWebsite")}
            name='webAppUrl'
          />
        </div>
        <div className='mb-3'>
          <TextField
            value={iconUrl || ""}
            onChange={handleIconUrlChange}
            fullWidth
            placeholder={`${t("editProduct.example")}: https://www.focus.no/wp-content/uploads/2023/04/GML_64.png`}
            label="Ikon url"
            name='iconUrl'
          />
        </div>
        <div className='mb-3'>
          <MultiSelect
            label={t("editProduct.vismaArticleID")}
            renderValue={(selected: IContractItemInfos[]) => selected.map(s => <div key={s.id}>{s.id}</div>)}
            options={freeVismaItems}
            value={selectedVismaItems}
            keyValue={"id"}
            displayValue={"id"}
            onChange={handleVismaIdChange}
          />
        </div>
        <div className='mb-3'>
          <MultiSelect
            label={t("standard.modules")}
            renderValue={(selected: IRelationProduct[]) => selected.map(s => <div key={s.id}>{s.productName}</div>)}
            options={props.allProducts}
            value={selectedModules}
            keyValue={"id"}
            displayValue={"productName"}
            onChange={handleModuleChange}
          />
        </div>
        <div className='mb-3'>
          <MultiSelect
            label={t("standard.systemRelatedProducts")}
            renderValue={(selected: IRelationProduct[]) => selected.map(s => <div key={s.id}>{s.productName}</div>)}
            options={props.allProducts}
            value={selectedRelatedProducts}
            keyValue={"id"}
            displayValue={"productName"}
            onChange={handleRelatedProductChange}
          />
        </div>
        <div className='mb-3'>
          <Select
            label={t("editProduct.supportCategory")}
            name='ticketCategory'
            fullWidth
            value={ticketCategoryId || ""}
            onChange={handleSupportTicketChange}>
            {categories.map(d => <MenuItem value={d.ticketCategoryId} key={d.ticketCategoryId}>{d.fullName}</MenuItem>)}
          </Select>
        </div>
        <div className='mb-3'>
          <TextField
            value={requirements || ""}
            onChange={handleRequirementsChange}
            placeholder={`${t("editProduct.example")}: Autocad, Cat Basis`}
            fullWidth
            label={t("editProduct.descriptionOfRequirements")}
            name='requirements'
          />
        </div>
        <div className='mb-3'>
          <MultiLineTextField
            value={description || ""}
            onChange={handleDescriptionChange}
            fullWidth
            label={t("editProduct.productDescription")}
            name='description'
            minRows={1}
          />
        </div>
        <div className='mb-3'>
          <TextField
            value={productPageUrl || ""}
            onChange={handleProductPageUrlChange}
            fullWidth
            label={t("editProduct.linkToProductPage")}
            name='productPageUrl'
          />
        </div>
        <div className='mb-3'>
          <CheckboxLabel
            checked={doesNotHaveNimbusLicensing}
            label={t("editProduct.notToBeLicensedInNimbus")}
            onChange={handleLicensingChange}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleProductUpdate}
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditProductDialog;